import React, { useState } from 'react';
import {
  Card,
  List,
  Avatar,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  Spin,
  message,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  EditOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { DetailedEmployeeResponse } from '../../../model/Employee.model';
import {
  updateUserInfo,
  UpdateUserRequestDto,
} from '../../../service/auth-service/auth-services';

interface BasicInfoProps {
  employee: DetailedEmployeeResponse;
  refreshData?: () => void;
}

const { Text, Title } = Typography;

const BasicInfo: React.FC<BasicInfoProps> = ({ employee, refreshData }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const { confirm } = Modal;

  // Kiểm tra nếu user hiện tại có quyền chỉnh sửa
  const canEdit = () => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    // Cho phép chỉnh sửa nếu là chính họ hoặc là admin
    return (
      currentUser.id === employee.user.id ||
      currentUser.roles?.some((role: any) => role.name === 'ROLE_ADMIN')
    );
  };

  const showModal = () => {
    form.setFieldsValue({
      email: employee.user.email,
      phone: employee.user.phone,
      fullName: employee.name,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setChangePassword(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      confirm({
        title: 'Xác nhận cập nhật thông tin',
        icon: <ExclamationCircleOutlined />,
        content: 'Bạn có chắc chắn muốn cập nhật thông tin này?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        onOk: async () => {
          try {
            setIsLoading(true);

            const updateData: UpdateUserRequestDto = {
              email: values.email,
              phone: values.phone,
              fullName: values.fullName,
            };

            if (changePassword && values.password) {
              updateData.password = values.password;
            }

            await updateUserInfo(employee.user.id, updateData);
            message.success('Cập nhật thông tin thành công');
            setIsModalVisible(false);
            setChangePassword(false);

            // Refresh data nếu có
            if (refreshData) {
              refreshData();
            }
          } catch (error: any) {
            message.error(error.message || 'Cập nhật thông tin thất bại');
          } finally {
            setIsLoading(false);
          }
        },
      });
    } catch (validationError) {
      // Form validation failed
      console.error('Validation failed:', validationError);
    }
  };

  return (
    <>
      <Card
        bordered
        title="Thông tin cơ bản"
        className="shadow-sm hover:shadow-md transition-shadow duration-300"
        extra={
          <Tooltip title="Chỉnh sửa thông tin nhân viên">
            {' '}
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={showModal}
            />
          </Tooltip>
        }
      >
        <div className="flex items-center mb-6 p-4 bg-gray-50 rounded-lg">
          <Avatar size={64} icon={<UserOutlined />} className="mr-6" />
          <div>
            <Title level={3} className="mb-1">
              {employee.name}
            </Title>
            <Text type="secondary" className="text-lg">
              {employee.position}
            </Text>
          </div>
        </div>
        <List className="divide-y divide-gray-100">
          <List.Item className="py-3">
            <div className="flex items-center">
              <IdcardOutlined className="mr-3 text-blue-500" />
              <span className="font-medium">ID:</span>
              <span className="ml-2">{employee.id}</span>
            </div>
          </List.Item>
          <List.Item className="py-3">
            <div className="flex items-center">
              <MailOutlined className="mr-3 text-blue-500" />
              <span className="font-medium">Email:</span>
              <span className="ml-2">{employee.user.email}</span>
            </div>
          </List.Item>
          <List.Item className="py-3">
            <div className="flex items-center">
              <PhoneOutlined className="mr-3 text-blue-500" />
              <span className="font-medium">Điện thoại:</span>
              <span className="ml-2">{employee.user.phone}</span>
            </div>
          </List.Item>
          <List.Item className="py-3">
            <div className="flex items-center">
              <UserOutlined className="mr-3 text-blue-500" />
              <span className="font-medium">Trạng thái:</span>
              <span className="ml-2">{employee.user.status}</span>
            </div>
          </List.Item>
        </List>
      </Card>

      {/* Modal chỉnh sửa thông tin */}
      <Modal
        title="Cập nhật thông tin cá nhân"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            onClick={handleSubmit}
          >
            Cập nhật
          </Button>,
        ]}
      >
        <Spin spinning={isLoading}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              email: employee.user.email,
              phone: employee.user.phone,
              fullName: employee.name,
            }}
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Email không hợp lệ' }]}
            >
              <Input prefix={<MailOutlined />} placeholder="Email" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Số điện thoại"
              rules={[
                {
                  pattern: /^\d{10,11}$/,
                  message: 'Số điện thoại không hợp lệ',
                },
              ]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="Số điện thoại" />
            </Form.Item>

            <Form.Item name="fullName" label="Họ và tên">
              <Input prefix={<UserOutlined />} placeholder="Họ và tên" />
            </Form.Item>

            <div className="mb-4">
              <Button
                type="link"
                onClick={() => setChangePassword(!changePassword)}
                icon={<LockOutlined />}
              >
                {changePassword ? 'Hủy đổi mật khẩu' : 'Đổi mật khẩu'}
              </Button>
            </div>

            {changePassword && (
              <Form.Item
                name="password"
                label="Mật khẩu mới"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu mới' },
                  { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Mật khẩu mới"
                />
              </Form.Item>
            )}
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default BasicInfo;
